import * as XLSX from "xlsx";
import { format, parseISO } from "date-fns";

/**
 * Helper untuk export data attendance ke Excel
 */
class AttendanceExcelExporter {
  static formatDate(dateString) {
    try {
      return format(parseISO(dateString), "dd MMM yyyy");
    } catch (error) {
      return dateString;
    }
  }

  static createAttendanceSheet(attendanceData) {
    const rows = attendanceData.map((item, index) => ({
      No: index + 1,
      Date: this.formatDate(item.attendance_date),
      Name: item.user_name,
      "In Time": item.actual_in_time || "-",
      "Out Time": item.actual_out_time || "-",
      Category:
        item.category === "Leave" ? item.leave_category : item.category,
      Notes: item.notes || "-",
      Location: item.coordinate_description || "-",
    }));

    return XLSX.utils.json_to_sheet(rows);
  }

  static getEffectiveCategory(item) {
    return item.category === "Leave"
      ? item.leave_category || "Leave"
      : item.category || "Unknown";
  }

  static calculateWorkingHours(inTime, outTime) {
    if (!inTime || !outTime || inTime === "-" || outTime === "-") return 0;

    const [inH, inM] = inTime.split(":").map(Number);
    const [outH, outM] = outTime.split(":").map(Number);
    if ([inH, inM, outH, outM].some((n) => Number.isNaN(n))) return 0;

    let diffMinutes = outH * 60 + outM - (inH * 60 + inM);
    if (diffMinutes < 0) diffMinutes += 24 * 60; // shift melewati tengah malam

    return diffMinutes / 60;
  }

  /**
   * Rekap per orang: total record per kategori & total jam kerja,
   * dihitung langsung dari data attendance yang diexport.
   */
  static buildPersonSummary(attendanceData) {
    const employees = new Map();

    attendanceData.forEach((item) => {
      const key = item.user_id ?? item.user_name;
      if (!employees.has(key)) {
        employees.set(key, {
          name: item.user_name,
          total: 0,
          categories: {},
          workingHours: 0,
          daysWithHours: 0,
        });
      }

      const emp = employees.get(key);
      emp.total += 1;

      const category = this.getEffectiveCategory(item);
      emp.categories[category] = (emp.categories[category] || 0) + 1;

      const hours = this.calculateWorkingHours(
        item.actual_in_time,
        item.actual_out_time
      );
      if (hours > 0) {
        emp.workingHours += hours;
        emp.daysWithHours += 1;
      }
    });

    const categoryKeys = Array.from(
      new Set(attendanceData.map((item) => this.getEffectiveCategory(item)))
    ).sort();

    const header = [
      "No",
      "Name",
      "Total Records",
      ...categoryKeys,
      "Total Working Hours",
      "Avg Working Hours/Day",
    ];

    const rows = Array.from(employees.values()).map((emp, index) => [
      index + 1,
      emp.name,
      emp.total,
      ...categoryKeys.map((key) => emp.categories[key] || 0),
      Math.round(emp.workingHours * 100) / 100,
      emp.daysWithHours > 0
        ? Math.round((emp.workingHours / emp.daysWithHours) * 100) / 100
        : 0,
    ]);

    return { header, rows, totalEmployees: employees.size };
  }

  static createSummarySheet(attendanceData, analyticsData, dateFrom, dateTo) {
    const personSummary = this.buildPersonSummary(attendanceData);
    const byDepartment = analyticsData?.by_department || [];

    const rows = [
      ["Attendance Summary"],
      ["Period", dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : "All"],
      ["Total Employees", personSummary.totalEmployees],
      ["Total Records", attendanceData.length],
      [],
      ["Employee Summary"],
      personSummary.header,
      ...personSummary.rows,
    ];

    if (byDepartment.length > 0) {
      const categoryKeys = Array.from(
        new Set(
          byDepartment.flatMap((dept) =>
            Object.keys(dept).filter((key) => key !== "department" && key !== "total")
          )
        )
      );

      rows.push([], ["By Department"], ["Department", ...categoryKeys, "Total"]);

      byDepartment.forEach((dept) => {
        rows.push([
          dept.department,
          ...categoryKeys.map((key) => dept[key] ?? 0),
          dept.total ?? 0,
        ]);
      });
    }

    return XLSX.utils.aoa_to_sheet(rows);
  }

  static applyColumnWidths(sheet) {
    if (!sheet["!ref"]) return;

    const range = XLSX.utils.decode_range(sheet["!ref"]);
    const colWidths = [];

    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = sheet[cellAddress];
        if (cell && cell.v) {
          const cellLength = cell.v.toString().length;
          if (cellLength > maxWidth) {
            maxWidth = Math.min(cellLength, 50);
          }
        }
      }
      colWidths.push({ wch: maxWidth + 2 });
    }

    sheet["!cols"] = colWidths;
  }

  /**
   * Export attendance data ke Excel (Attendance sheet + Summary sheet)
   */
  static exportToExcel(attendanceData, attendanceInfo = {}, analyticsData = null) {
    try {
      const wb = XLSX.utils.book_new();

      const sheet = this.createAttendanceSheet(attendanceData);
      this.applyColumnWidths(sheet);
      XLSX.utils.book_append_sheet(wb, sheet, "Attendance");

      const summarySheet = this.createSummarySheet(
        attendanceData,
        analyticsData,
        attendanceInfo.date_from,
        attendanceInfo.date_to
      );
      XLSX.utils.book_append_sheet(wb, summarySheet, "Summary");

      const dateFrom = attendanceInfo.date_from || "";
      const dateTo = attendanceInfo.date_to || "";
      const rangeLabel =
        dateFrom && dateTo ? `${dateFrom}_to_${dateTo}` : "All";
      const filename = `Attendance_${rangeLabel}.xlsx`;

      XLSX.writeFile(wb, filename);

      return {
        success: true,
        filename,
        message: "Attendance exported successfully",
      };
    } catch (error) {
      console.error("Export attendance to Excel failed:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to export attendance",
      };
    }
  }
}

export default AttendanceExcelExporter;
